import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useCommandPalette } from "@/hooks/use-command-palette";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import {
  Users,
  FileText,
  FileSignature,
  Calendar,
  LayoutDashboard,
  Mail,
  BarChart3,
  Telescope,
  Megaphone,
  CalendarDays,
  Mic2,
  ListMusic,
  DollarSign,
  Send,
  Plus,
  Search as SearchIcon,
  Receipt,
  UserPlus,
  ClipboardCheck,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";

interface LeadHit {
  id: string;
  recordId: string | null;
  leadName: string | null;
  contactName: string | null;
  emailAddress: string | null;
  city: string | null;
  state: string | null;
}
interface ClientHit {
  id: string;
  recordId: string | null;
  accountName: string | null;
  contactName: string | null;
  emailAddress: string | null;
  city: string | null;
  state: string | null;
}
interface InvoiceHit {
  id: string;
  recordId: string | null;
  invoiceNumber: string | null;
  accountName: string | null;
  billingContactName: string | null;
  billingEmail: string | null;
  status: string | null;
}
interface GlobalSearchResponse {
  items: { leads: LeadHit[]; clients: ClientHit[]; invoices: InvoiceHit[] };
  suggestions: string[];
}

interface NavCommand {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  permissionKey?: string;
  keywords?: string[];
}

interface ActionCommand {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  permissionKey?: string;
  keywords?: string[];
}

const NAV_COMMANDS: NavCommand[] = [
  { id: "nav-dashboard", label: "Dashboard", icon: LayoutDashboard, href: "/", permissionKey: "dashboard.module.visible", keywords: ["home", "overview"] },
  { id: "nav-leads", label: "Leads", icon: Users, href: "/leads", permissionKey: "leads.module.visible" },
  { id: "nav-clients", label: "Clients", icon: Users, href: "/clients", permissionKey: "clients.module.visible" },
  { id: "nav-invoices", label: "Invoices", icon: FileText, href: "/invoices", permissionKey: "invoices.module.visible" },
  { id: "nav-billing", label: "Billing", icon: DollarSign, href: "/billing", permissionKey: "billing.module.visible" },
  { id: "nav-schedule", label: "Master Schedule", icon: Calendar, href: "/master-schedule", permissionKey: "schedule.module.visible" },
  { id: "nav-calendars", label: "Calendar Hub", icon: CalendarDays, href: "/calendars", permissionKey: "calendarHub.module.visible" },
  { id: "nav-email-hub", label: "Email Hub", icon: Mail, href: "/email-hub", permissionKey: "communications.module.visible", keywords: ["communications", "campaigns"] },
  { id: "nav-marketing-contacts", label: "Marketing Contacts", icon: Megaphone, href: "/marketing/contacts", permissionKey: "marketing.feature.viewContacts" },
  // Permission keys MUST match the ProtectedRoute guard on each route in
  // App.tsx — otherwise the palette would surface a Go-To item that lands
  // the user on a NotAuthorized page. Verified against App.tsx route
  // definitions on 2026-04-25.
  { id: "nav-marketing-social", label: "Marketing — Social", icon: Send, href: "/marketing/social", permissionKey: "socialMedia.module.visible" },
  { id: "nav-reports", label: "Reports", icon: BarChart3, href: "/reports", permissionKey: "reports.module.visible" },
  { id: "nav-lead-gen", label: "Lead Gen", icon: Telescope, href: "/lead-gen", permissionKey: "leadGen.module.visible" },
  { id: "nav-forms", label: "Forms", icon: FileSignature, href: "/forms", permissionKey: "forms.module.visible" },
  { id: "nav-profiles", label: "Talent Profiles", icon: Mic2, href: "/profiles", permissionKey: "profiles.feature.view" },
  { id: "nav-events", label: "Events", icon: ListMusic, href: "/events", permissionKey: "myEvents.module.visible" },
  { id: "nav-approvals", label: "Approvals", icon: ClipboardCheck, href: "/intake", permissionKey: "approvals.module.visible" },
  { id: "nav-admin", label: "Admin Settings", icon: SettingsIcon, href: "/admin", permissionKey: "adminPanel.module.visible" },
];

const ACTION_COMMANDS: ActionCommand[] = [
  { id: "act-new-lead", label: "New Lead", icon: UserPlus, href: "/leads?new=1", permissionKey: "leads.feature.create" },
  { id: "act-new-client", label: "New Client", icon: UserPlus, href: "/clients?new=1", permissionKey: "clients.feature.create" },
  { id: "act-new-invoice", label: "New Invoice", icon: Receipt, href: "/invoices?new=1", permissionKey: "invoices.feature.create" },
  { id: "act-log-event", label: "Log an Event", icon: Plus, href: "/clients?logShow=1", permissionKey: "clients.feature.create", keywords: ["booking", "shows"] },
  { id: "act-compose-email", label: "Compose Email", icon: Mail, href: "/email-hub?compose=1", permissionKey: "communications.module.visible" },
];

function leadSecondary(l: LeadHit): string | null {
  const parts = [l.contactName, [l.city, l.state].filter(Boolean).join(", ") || null, l.emailAddress];
  return parts.filter((s): s is string => !!s).join(" · ") || null;
}
function clientSecondary(c: ClientHit): string | null {
  const parts = [c.contactName, [c.city, c.state].filter(Boolean).join(", ") || null, c.emailAddress];
  return parts.filter((s): s is string => !!s).join(" · ") || null;
}
function invoiceSecondary(i: InvoiceHit): string | null {
  const parts: Array<string | null> = [];
  if (i.invoiceNumber && i.invoiceNumber !== i.accountName) parts.push(`#${i.invoiceNumber}`);
  if (i.billingContactName) parts.push(i.billingContactName);
  if (i.status) parts.push(i.status);
  return parts.filter((s): s is string => !!s).join(" · ") || null;
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const { permissions } = useEffectivePermissions();

  // Reset the input whenever the dialog reopens so users start fresh.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Debounce so we don't fire a request per keystroke. cmdk re-renders on
  // every input change anyway; 200ms keeps it snappy without overloading
  // the server during long bursts of typing.
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const enabledForSearch = open && debouncedQuery.length >= 2;
  const { data: searchResults, isFetching } = useQuery<GlobalSearchResponse>({
    queryKey: ["/api/search/global", debouncedQuery],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/search/global?q=${encodeURIComponent(debouncedQuery)}`, {
        credentials: "include",
        signal,
      });
      if (!res.ok) throw new Error(`Search failed: ${res.status}`);
      return res.json();
    },
    enabled: enabledForSearch,
    staleTime: 30_000,
  });

  const allowed = (permissionKey?: string) => {
    if (!permissionKey) return true;
    if (!permissions) return false;
    return permissions[permissionKey] === true;
  };

  const visibleNav = NAV_COMMANDS.filter((cmd) => allowed(cmd.permissionKey));
  const visibleActions = ACTION_COMMANDS.filter((cmd) => allowed(cmd.permissionKey));

  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  // Defensively normalize the response — a malformed payload (e.g. partial
  // shape from a future API change) shouldn't crash the palette.
  const items = {
    leads: searchResults?.items?.leads ?? [],
    clients: searchResults?.items?.clients ?? [],
    invoices: searchResults?.items?.invoices ?? [],
  };
  const totalResults = items.leads.length + items.clients.length + items.invoices.length;
  const suggestions = searchResults?.suggestions ?? [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      {/* Visually-hidden title + description so the underlying Radix Dialog
          satisfies WCAG (every dialog needs an accessible name). The visual
          UI uses the search input as the focal element instead of a heading. */}
      <VisuallyHidden>
        <DialogTitle>Command palette</DialogTitle>
        <DialogDescription>
          Search across leads, clients, and invoices, or jump to any page.
        </DialogDescription>
      </VisuallyHidden>
      <CommandInput
        placeholder="Search records, jump to a page, or run an action..."
        value={query}
        onValueChange={setQuery}
        data-testid="input-command-palette"
      />
      <CommandList data-testid="list-command-palette">
        <CommandEmpty>
          {enabledForSearch && isFetching ? "Searching…" : "No matches. Try a different query."}
        </CommandEmpty>

        {enabledForSearch && totalResults === 0 && !isFetching && suggestions.length > 0 && (
          <CommandGroup heading="Did you mean…?">
            {suggestions.map((s) => (
              <CommandItem
                key={`sugg-${s}`}
                value={`suggestion ${s}`}
                onSelect={() => setQuery(s)}
                data-testid={`palette-suggestion-${s}`}
              >
                <SearchIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{s}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {enabledForSearch && totalResults > 0 && (
          <>
            {items.leads.length > 0 && (
              <CommandGroup heading="Leads">
                {items.leads.map((r) => {
                  const primary = r.leadName || r.contactName || r.emailAddress || r.recordId || "Untitled lead";
                  const secondary = leadSecondary(r);
                  return (
                    <CommandItem
                      key={`lead-${r.id}`}
                      value={`lead ${r.recordId ?? ""} ${primary} ${secondary ?? ""}`}
                      onSelect={() => go(`/leads?openLead=${r.id}`)}
                      data-testid={`palette-result-lead-${r.id}`}
                    >
                      <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{primary}</span>
                        {secondary && <span className="text-xs text-muted-foreground truncate">{secondary}</span>}
                      </div>
                      {r.recordId && (
                        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{r.recordId}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {items.clients.length > 0 && (
              <CommandGroup heading="Clients">
                {items.clients.map((r) => {
                  const primary = r.accountName || r.contactName || r.emailAddress || r.recordId || "Untitled client";
                  const secondary = clientSecondary(r);
                  return (
                    <CommandItem
                      key={`client-${r.id}`}
                      value={`client ${r.recordId ?? ""} ${primary} ${secondary ?? ""}`}
                      onSelect={() => go(`/clients?openClient=${r.id}`)}
                      data-testid={`palette-result-client-${r.id}`}
                    >
                      <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{primary}</span>
                        {secondary && <span className="text-xs text-muted-foreground truncate">{secondary}</span>}
                      </div>
                      {r.recordId && (
                        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{r.recordId}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {items.invoices.length > 0 && (
              <CommandGroup heading="Invoices">
                {items.invoices.map((r) => {
                  const primary = r.accountName || (r.invoiceNumber ? `Invoice ${r.invoiceNumber}` : null) || r.recordId || "Untitled invoice";
                  const secondary = invoiceSecondary(r);
                  return (
                    <CommandItem
                      key={`invoice-${r.id}`}
                      value={`invoice ${r.recordId ?? ""} ${primary} ${secondary ?? ""}`}
                      onSelect={() => go(`/invoices?openInvoice=${r.id}`)}
                      data-testid={`palette-result-invoice-${r.id}`}
                    >
                      <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{primary}</span>
                        {secondary && <span className="text-xs text-muted-foreground truncate">{secondary}</span>}
                      </div>
                      {r.recordId && (
                        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{r.recordId}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            <CommandSeparator />
          </>
        )}

        {visibleActions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {visibleActions.map((cmd) => {
              const Icon = cmd.icon;
              return (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${(cmd.keywords ?? []).join(" ")}`}
                  onSelect={() => go(cmd.href)}
                  data-testid={`palette-action-${cmd.id}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{cmd.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {visibleNav.length > 0 && (
          <CommandGroup heading="Go to">
            {visibleNav.map((cmd) => {
              const Icon = cmd.icon;
              return (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${(cmd.keywords ?? []).join(" ")}`}
                  onSelect={() => go(cmd.href)}
                  data-testid={`palette-nav-${cmd.id}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{cmd.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
      <div className="flex items-center justify-between border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1">
          <SearchIcon className="h-3 w-3" />
          <span>Type to search records or jump to a page</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline">Open with</span>
          <kbd className="rounded border bg-muted px-1 font-mono">⌘⇧K</kbd>
          <kbd className="rounded border bg-muted px-1 font-mono">Esc</kbd>
        </div>
      </div>
    </CommandDialog>
  );
}
